import React, { useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';

import { AddButton } from 'components/Button';
import { Search } from 'components/Search';

import { useLots } from '../api/getLots';
import { useDeleteLot } from '../api/deleteLot';
import { useReorderLots } from '../api/reorderLots';
import { useDragOrder } from '../hooks/useDragOrder';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { LotFormModal } from './LotFormModal';
import { LotListItem } from './LotListItem';
import { LotSuppliersModal } from './LotSuppliersModal';
import { RolesView } from './RolesView';
import { SetupOverlay } from './SetupOverlay';

export const LotsOverlay = ({ resource, id, procurementName, onClose }) => {
  const withSuppliers = resource === 'framework_agreements';
  const [editedLot, setEditedLot] = useState(null);
  const [suppliersLot, setSuppliersLot] = useState(null);
  const [deletedLot, setDeletedLot] = useState(null);
  const [search, setSearch] = useState('');
  const [rolesLotId, setRolesLotId] = useState(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const isModalOpen = !!editedLot || !!suppliersLot || !!deletedLot || isRoleModalOpen;

  const { data: lots, refetch, isFetchedAfterMount } = useLots(resource, id);
  const { mutateAsync: deleteLot } = useDeleteLot(resource, id, () => {
    refetch();
    setDeletedLot(null);
  });
  const { mutateAsync: reorderLots } = useReorderLots(resource, id, refetch);
  const { order, draggedId, dragHandlers } = useDragOrder(lots, 'lot_id', reorderLots);

  const searchTerm = search.trim().toLowerCase();
  const filteredLots = order.filter(
    ({ name, description }) =>
      name.toLowerCase().includes(searchTerm) || description.toLowerCase().includes(searchTerm)
  );
  const canDrag = !searchTerm && order.length > 1;

  const rolesLot = lots.find(({ lot_id }) => lot_id === rolesLotId);
  const crumbs = rolesLot
    ? [
        { label: 'Lots', onClick: () => setRolesLotId(null) },
        { label: rolesLot.name },
        { label: 'Roles' },
      ]
    : [{ label: 'Lots' }];

  return (
    <SetupOverlay
      procurementName={procurementName}
      crumbs={crumbs}
      isModalOpen={isModalOpen}
      onClose={onClose}
    >
      {rolesLot ? (
        <RolesView
          resource={resource}
          id={id}
          lot={rolesLot}
          onChanged={refetch}
          onModalChange={setIsRoleModalOpen}
        />
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Search
              handleSearchChange={({ target: { value } }) => setSearch(value)}
              sx={{ minWidth: 200, width: 270 }}
            />
            <AddButton
              onClick={() => setEditedLot({})}
              sx={({ palette }) => ({
                svg: { mt: -0.5 },
                minWidth: 200,
                border: `1px solid ${palette.grey[200]}`,
              })}
              color="secondary"
            >
              Add lot
            </AddButton>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {isFetchedAfterMount && filteredLots.length === 0 && (
            <Stack flex={1} alignItems="center" justifyContent="center" gap={1}>
              <Typography variant="h5" fontWeight={700}>
                {searchTerm ? 'No lots found' : 'No lots yet'}
              </Typography>
              <Typography color="text.secondary">
                {searchTerm ? 'Please try another search term' : 'Click "Add lot" to begin'}
              </Typography>
            </Stack>
          )}

          <Stack gap={1}>
            {filteredLots.map((lot) => (
              <LotListItem
                key={lot.lot_id}
                withSuppliers={withSuppliers}
                canDrag={canDrag}
                isDragged={draggedId === lot.lot_id}
                dragHandlers={dragHandlers(lot.lot_id)}
                markForEdit={() => setEditedLot(lot)}
                markForDelete={lot.role_count ? undefined : () => setDeletedLot(lot)}
                openSuppliers={() => setSuppliersLot(lot)}
                openRoles={() => {
                  setSearch('');
                  setRolesLotId(lot.lot_id);
                }}
                {...lot}
              />
            ))}
          </Stack>
        </>
      )}

      <LotFormModal
        resource={resource}
        id={id}
        lot={editedLot}
        open={!!editedLot}
        handleClose={() => setEditedLot(null)}
        onSaved={refetch}
      />

      {withSuppliers && (
        <LotSuppliersModal
          frameworkAgreementId={id}
          lot={suppliersLot}
          handleClose={() => setSuppliersLot(null)}
          onSaved={refetch}
        />
      )}

      <DeleteConfirmModal
        title="Delete lot"
        label="lot"
        name={deletedLot?.name}
        open={!!deletedLot}
        handleClose={() => setDeletedLot(null)}
        onConfirm={() => deleteLot(deletedLot.lot_id).catch(() => {})}
      />
    </SetupOverlay>
  );
};
